import {
  UseMutationOptions,
  UseMutationResult,
  UseQueryOptions,
  UseQueryResult,
  useMutation,
  useQuery,
} from '@tanstack/react-query';
import {
  CreateNotePayload,
  DeleteNotePayload,
  Note,
  NoteAPI,
  NoteSource,
  NoteSourceType,
  UpdateNotePayload,
} from 'api/note';

type AllNotesBySourceKeyType = ['notes', 'by-source', NoteSourceType, number];
export const AllNotesBySourceKey = (
  source: NoteSource,
): AllNotesBySourceKeyType => [
  'notes',
  'by-source',
  source.sourceType,
  source.sourceId,
];
export type GetAllNotesQueryOption = UseQueryOptions<
  Note[],
  unknown,
  Note[],
  AllNotesBySourceKeyType
>;
export const useGetAllNotesBySource = (
  source: NoteSource,
  options: GetAllNotesQueryOption = {},
): UseQueryResult<Note[], unknown> => {
  return useQuery<Note[], unknown, Note[], AllNotesBySourceKeyType>(
    AllNotesBySourceKey(source),
    () => NoteAPI.getAllNotesBySource(source),
    options,
  );
};

export const useGetAllCompanyNotes = (
  source: NoteSource,
  options: GetAllNotesQueryOption = {},
): UseQueryResult<Note[], unknown> => {
  return useQuery<Note[], unknown, Note[], AllNotesBySourceKeyType>(
    AllNotesBySourceKey(source),
    () => NoteAPI.getAllNotesBySource(source),
    options,
  );
};

export const useGetAllProjectNotes = (
  source: NoteSource,
  options: GetAllNotesQueryOption = {},
): UseQueryResult<Note[], unknown> => {
  return useQuery<Note[], unknown, Note[], AllNotesBySourceKeyType>(
    AllNotesBySourceKey(source),
    () => NoteAPI.getAllProjectNotes(source),
    options,
  );
};

export const useCreateNoteMutation = (
  options: UseMutationOptions<Note, unknown, CreateNotePayload> = {},
): UseMutationResult<Note, unknown, CreateNotePayload> =>
  useMutation<Note, unknown, CreateNotePayload>(
    (payload) =>
      payload.associatedEntity.sourceType === NoteSourceType.Company
        ? NoteAPI.createNote(payload)
        : NoteAPI.createProjectNote(payload),
    options,
  );

export const useUpdateNoteMutation = (
  options: UseMutationOptions<Note, unknown, UpdateNotePayload> = {},
): UseMutationResult<Note, unknown, UpdateNotePayload> => {
  return useMutation<Note, unknown, UpdateNotePayload>(
    (payload) =>
      payload.note.noteType === NoteSourceType.Company
        ? NoteAPI.updateNote(payload)
        : NoteAPI.updateProjectNote(
            payload,
            payload.note.noteType as NoteSourceType,
            payload.note.sourceId ?? -1,
          ),
    options,
  );
};

export const useDeleteNoteMutation = (
  options: UseMutationOptions<void, unknown, DeleteNotePayload> = {},
): UseMutationResult<void, unknown, DeleteNotePayload> => {
  return useMutation<void, unknown, DeleteNotePayload>(
    ({ noteId, noteType, sourceId }) =>
      noteType === NoteSourceType.Company
        ? NoteAPI.deleteNote(noteId)
        : NoteAPI.deleteProjectNote(
            noteId,
            noteType as NoteSourceType,
            sourceId ?? -1,
          ),
    options,
  );
};
